<template>
    <div :class="wrapperClasses">
        <div ref="gutter" :class="gutterClasses">
            <div
                v-for="line in lineNumbers"
                :key="'mail-html-line-' + line"
                :class="lineNumberClasses"
            >
                {{ line }}
            </div>
        </div>
        <div :class="contentClasses">
            <div ref="highlight" aria-hidden="true" :class="highlightClasses">
                <div
                    v-for="(line, index) in highlightedLines"
                    :key="'mail-html-highlight-' + index"
                    :class="highlightLineClasses"
                    v-html="line || '&nbsp;'"
                ></div>
            </div>
            <textarea
                ref="textarea"
                :value="modelValue"
                :rows="rows"
                :readonly="readonly"
                :class="textareaClasses"
                spellcheck="false"
                wrap="off"
                @input="onInput"
                @scroll="syncScroll"
            ></textarea>
        </div>
    </div>
</template>

<script>
export default {
    name: "HtmlCodeEditor",
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        rows: {
            type: Number,
            default: 18,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: 'default',
        },
        fill: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    computed: {
        isVaultVariant() {
            return this.variant === 'vault';
        },
        wrapperClasses() {
            return [
                this.isVaultVariant ? 'vault-html-editor' : 'mail-html-editor',
                {'vault-html-editor--fill': this.isVaultVariant && this.fill},
            ];
        },
        gutterClasses() {
            return this.isVaultVariant ? 'vault-html-editor__gutter' : 'mail-html-editor__gutter';
        },
        lineNumberClasses() {
            return this.isVaultVariant ? 'vault-html-editor__line-number' : 'mail-html-editor__line-number';
        },
        contentClasses() {
            return this.isVaultVariant ? 'vault-html-editor__content' : 'mail-html-editor__content';
        },
        highlightClasses() {
            return this.isVaultVariant ? 'vault-html-editor__highlight' : 'mail-html-editor__highlight';
        },
        highlightLineClasses() {
            return this.isVaultVariant ? 'vault-html-editor__highlight-line' : 'mail-html-editor__highlight-line';
        },
        textareaClasses() {
            return this.isVaultVariant ? 'vault-html-editor__textarea' : 'mail-html-editor__textarea';
        },
        lines() {
            const normalized = String(this.modelValue || '').replace(/\r\n/g, '\n');
            return normalized.split('\n');
        },
        lineNumbers() {
            const count = Math.max(1, this.lines.length);
            return Array.from({length: count}, (_, index) => index + 1);
        },
        highlightedLines() {
            return this.lines.map((line) => this.highlightHtml(line));
        },
    },
    methods: {
        onInput(event) {
            this.$emit('update:modelValue', event.target.value);
        },
        syncScroll() {
            const textarea = this.$refs.textarea;
            const highlight = this.$refs.highlight;
            const gutter = this.$refs.gutter;
            if (!textarea || !highlight || !gutter) {
                return;
            }

            highlight.scrollTop = textarea.scrollTop;
            highlight.scrollLeft = textarea.scrollLeft;
            gutter.scrollTop = textarea.scrollTop;
        },
        insertAtCursor(content) {
            const textarea = this.$refs.textarea;
            const current = String(this.modelValue || '');
            if (!textarea) {
                this.$emit('update:modelValue', current + content);
                return;
            }

            const start = typeof textarea.selectionStart === 'number' ? textarea.selectionStart : current.length;
            const end = typeof textarea.selectionEnd === 'number' ? textarea.selectionEnd : current.length;
            const nextValue = current.slice(0, start) + content + current.slice(end);

            this.$emit('update:modelValue', nextValue);

            this.$nextTick(() => {
                if (!this.$refs.textarea) {
                    return;
                }
                const cursorPos = start + content.length;
                this.$refs.textarea.focus();
                this.$refs.textarea.setSelectionRange(cursorPos, cursorPos);
            });
        },
        highlightHtml(line) {
            let next = this.escapeHtml(line);

            next = next.replace(/(&lt;!--.*?--&gt;)/g, '<span class="mhe-token mhe-token-comment">$1</span>');
            next = next.replace(/(&lt;!DOCTYPE.*?&gt;)/gi, '<span class="mhe-token mhe-token-punct">$1</span>');
            next = next.replace(
                /(&lt;\/?)([A-Za-z_][A-Za-z0-9_.:-]*)(.*?)(\/?&gt;)/g,
                (_, open, tagName, attributes, close) => {
                    const attrs = attributes.replace(
                        /([A-Za-z_:][-A-Za-z0-9_:.]*)(\s*=\s*)("[^"]*"|'[^']*')/g,
                        '<span class="mhe-token mhe-token-attr">$1</span><span class="mhe-token mhe-token-punct">$2</span><span class="mhe-token mhe-token-string">$3</span>'
                    );

                    return `<span class="mhe-token mhe-token-punct">${open}</span><span class="mhe-token mhe-token-tag">${tagName}</span>${attrs}<span class="mhe-token mhe-token-punct">${close}</span>`;
                }
            );

            return next;
        },
        escapeHtml(value) {
            return String(value || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        },
    },
};
</script>

<style scoped>
.mail-html-editor {
    display: flex;
    width: 100%;
    min-height: 26rem;
    border: 1px solid var(--ui-border-subtle);
    border-radius: 0.375rem;
    background: #ffffff;
    overflow: hidden;
}

.mail-html-editor__gutter {
    flex: 0 0 56px;
    width: 56px;
    overflow: hidden;
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
    color: var(--ui-text-muted);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    line-height: 1.55;
    padding: 12px 8px 40px 0;
    text-align: right;
    user-select: none;
}

.mail-html-editor__line-number {
    height: 1.55em;
}

.mail-html-editor__content {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
}

.mail-html-editor__highlight {
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 12px 14px 40px;
    overflow: auto;
    box-sizing: border-box;
    color: var(--ui-text-heading);
    background: #ffffff;
    pointer-events: none;
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
}

.mail-html-editor__highlight-line {
    min-height: 1.55em;
    white-space: pre;
}

.mail-html-editor__textarea {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    outline: 0;
    resize: none;
    margin: 0;
    padding: 12px 14px 40px;
    overflow: auto;
    box-sizing: border-box;
    white-space: pre;
    background: transparent;
    color: transparent;
    -webkit-text-fill-color: transparent;
    caret-color: var(--ui-text-heading);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
}

.mail-html-editor__textarea::selection {
    background: rgba(59, 130, 246, 0.25);
    color: transparent;
}

.vault-html-editor {
    display: flex;
    width: 100%;
    min-height: 26rem;
    height: auto;
    background: #ffffff;
    overflow: hidden;
}

.vault-html-editor--fill {
    min-height: 100%;
    height: 100%;
}

.vault-html-editor__gutter {
    flex: 0 0 56px;
    width: 56px;
    overflow: hidden;
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
    color: var(--ui-text-muted);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    line-height: 1.55;
    padding: 12px 8px 40px 0;
    text-align: right;
    user-select: none;
}

.vault-html-editor__line-number {
    height: 1.55em;
}

.vault-html-editor__content {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
}

.vault-html-editor__highlight {
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 12px 14px 40px;
    overflow: auto;
    box-sizing: border-box;
    color: var(--ui-text-heading);
    background: #ffffff;
    pointer-events: none;
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
}

.vault-html-editor__highlight-line {
    min-height: 1.55em;
    white-space: pre;
}

.vault-html-editor__textarea {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    outline: 0;
    resize: none;
    margin: 0;
    padding: 12px 14px 40px;
    overflow: auto;
    box-sizing: border-box;
    white-space: pre;
    background: transparent;
    color: transparent;
    -webkit-text-fill-color: transparent;
    caret-color: var(--ui-text-heading);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
}

.vault-html-editor__textarea::selection {
    background: rgba(59, 130, 246, 0.25);
    color: transparent;
}
</style>

<style>
.mhe-token-tag {
    color: #0f766e;
    font-weight: 600;
}

.mhe-token-attr {
    color: #9a3412;
}

.mhe-token-string {
    color: #047857;
}

.mhe-token-comment {
    color: var(--ui-text-muted);
    font-style: italic;
}

.mhe-token-punct {
    color: var(--ui-text-muted);
}

.vault-html-editor .mhe-token-tag {
    color: #0f766e;
    font-weight: 600;
}

.vault-html-editor .mhe-token-attr {
    color: #9a3412;
}

.vault-html-editor .mhe-token-string {
    color: #047857;
}

.vault-html-editor .mhe-token-comment {
    color: var(--ui-text-muted);
    font-style: italic;
}

.vault-html-editor .mhe-token-punct {
    color: var(--ui-text-muted);
}
</style>
